/**
 * Where the operator was headed before Auth0 took the tab.
 *
 * ## The bug this closes
 *
 * `withAuthenticationRequired` already puts the attempted path into
 * `appState.returnTo`, and the Auth0 SDK already round-trips it back through
 * the redirect. Nothing consumed it: `callback.tsx` ran
 * `navigate('/', { replace: true })` unconditionally, `/` redirects to
 * `/dashboard`, and so **every deep link into this console landed on the
 * dashboard** — a link to a case, a disruption, or `/contracts/new` pasted into
 * Slack silently dropped its destination. It looked like the route was missing.
 *
 * The SDK's own `defaultOnRedirectCallback` does a `history.replaceState` to
 * `returnTo`, which is why the address bar was briefly right before the
 * navigate overwrote it — the destination was always there, just discarded.
 *
 * ## Why a module holder rather than storage
 *
 * `onRedirectCallback` fires inside the SDK's `handleRedirectCallback`, before
 * it dispatches the state update that flips `isAuthenticated` — so the value is
 * always set before `CallbackPage`'s effect reads it, in the same page load.
 * Nothing needs to outlive that, and a `sessionStorage` key that did would be a
 * stale destination waiting for the next login.
 */

let pending: string | null = null;

/**
 * Accept only a same-origin, in-app path.
 *
 * `returnTo` reaches us from `appState`, which round-trips through the
 * authorize request — so it is attacker-influenceable in the general case, and
 * feeding it to `navigate()` unchecked is an open redirect. Three rules:
 *
 * - it must start with `/` (rejects `https://evil.example`, `javascript:…`);
 * - it must not start with `//` (protocol-relative — `//evil.example` is a
 *   *different origin* that otherwise looks like a path);
 * - it must not be an auth route, or signing in bounces through itself.
 */
export function safeReturnTo(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const path = raw.trim();
  if (!path.startsWith('/') || path.startsWith('//')) return null;
  if (/^\/(login|callback|goodbye|forbidden)\b/.test(path)) return null;
  return path;
}

/** Called from `Auth0Provider`'s `onRedirectCallback`. */
export function rememberReturnTo(raw: unknown): void {
  pending = safeReturnTo(raw);
}

/** Read once and clear, so a later sign-in cannot inherit this destination. */
export function takeReturnTo(): string | null {
  const value = pending;
  pending = null;
  return value;
}

/**
 * The path to come back to, captured at the moment the shell demands a login.
 *
 * Includes the query string: `?view=attention` and `?airports=SCL,LIM` are how
 * half this console's screens are actually shared, and a returnTo that dropped
 * them would fix the route and still lose the view.
 */
export function currentReturnTo(): string {
  return `${window.location.pathname}${window.location.search}`;
}
